import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const serviceImages = [
  "https://net-basis.net/Bilder/ict-business-development-strategy-dach-market-entry.webp",
  "https://net-basis.net/Bilder/ict-market-entry-consulting-strategy-dach.webp",
  "https://net-basis.net/Bilder/ict-interim-management-experts-dach-market-entry.webp",
  "https://net-basis.net/Bilder/ict-training-coaching-dach-market-entry.webp",
];

/* URLs by language — business-development card is language-prefixed, other cards keep unprefixed routes for now */
const bdPathByLang: Record<string, string> = {
  de: "/de/services/business-development",
  en: "/en/services/business-development",
  fr: "/fr/services/developpement-commercial",
  es: "/es/servicios/desarrollo-de-negocio",
};

const consultingPathByLang: Record<string, string> = {
  de: "/de/services/consulting",
  en: "/en/services/consulting-services",
  fr: "/fr/services/conseil",
  es: "/es/servicios/consultoria",
};

const interimPathByLang: Record<string, string> = {
  de: "/de/services/interim-management",
  en: "/en/services/interim-management",
  fr: "/fr/services/management-de-transition",
  es: "/es/servicios/interim-management",
};

const trainingPathByLang: Record<string, string> = {
  de: "/de/services/training-coaching",
  en: "/en/services/training-coaching",
  fr: "/fr/services/formation-et-coaching",
  es: "/es/servicios/formacion-y-coaching",
};

interface ServiceCardsProps {
  titleKeys: string[];
  descriptionKeys: string[];
  readMoreKey: string;
  namespace: string;
  texts?: {
    titles: string[];
    descriptions: string[];
    readMore: string;
  };
}

export default function ServiceCards({ titleKeys, descriptionKeys, readMoreKey, namespace, texts }: ServiceCardsProps) {
  const { t, i18n } = useTranslation(namespace);
  const lang = (i18n.language || "de").split("-")[0];

  const services = [
    { id: 1, titleKey: titleKeys[0], descriptionKey: descriptionKeys[0], href: bdPathByLang[lang] || bdPathByLang.de },
    { id: 2, titleKey: titleKeys[1], descriptionKey: descriptionKeys[1], href: consultingPathByLang[lang] || consultingPathByLang.de },
    { id: 3, titleKey: titleKeys[2], descriptionKey: descriptionKeys[2], href: interimPathByLang[lang] || interimPathByLang.de },
    { id: 4, titleKey: titleKeys[3], descriptionKey: descriptionKeys[3], href: trainingPathByLang[lang] || trainingPathByLang.de },
  ];

  const getTitle = (idx: number) => texts ? texts.titles[idx] : t(titleKeys[idx]);
  const getDescription = (idx: number) => texts ? texts.descriptions[idx] : t(descriptionKeys[idx]);
  const getReadMore = () => texts ? texts.readMore : t(readMoreKey);

  return (
    <section className="py-5 md:py-7 px-4 md:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 items-stretch">
          {services.map((service, idx) => (
            <div
              key={service.id}
              className="group bg-white rounded-lg overflow-hidden border border-background-200 transition-all duration-300 hover:border-primary-300 flex flex-col h-full"
            >
              <div className="h-44 md:h-52 overflow-hidden flex-shrink-0">
                <img
                  src={serviceImages[service.id - 1]}
                  alt={getTitle(idx)}
                  title={getTitle(idx)}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>

              <div className="p-4 md:p-5 flex flex-col flex-1">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-2">
                  {getTitle(idx)}
                </h3>
                <p className="text-sm text-foreground-950 leading-relaxed mb-4 line-clamp-4 flex-1">
                  {getDescription(idx)}
                </p>
                <Link
                  to={service.href}
                  className="inline-flex items-center gap-1 px-4 py-2 font-heading text-sm font-medium text-primary-600 border border-primary-300 rounded-md hover:bg-primary-500 hover:text-white hover:border-primary-500 transition-colors cursor-pointer whitespace-nowrap mt-auto self-start"
                >
                  {getReadMore()}
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}